import { useState, useEffect, useMemo, useRef, Fragment } from "react";

import { API } from "../config";

const FONT = "'DM Sans', sans-serif";

// 11 leghe ufficiali del perimetro
const LEGHE = [
  "Belgian First Division A",
  "Dutch Eredivisie",
  "English Championship",
  "English Premier League",
  "French Ligue 1",
  "German Bundesliga",
  "German Bundesliga 2",
  "Italian Serie A",
  "Portuguese Primeira Liga",
  "Spanish La Liga",
  "Turkish Super League",
];

// D367 — Link Dropbox della procedura cambio stagione di Investisport Quote
const LINK_PROCEDURA_QUOTE =
  "https://www.dropbox.com/scl/fi/x44pdg4sbxijani41912l/2-Procedura_Pausa_Estiva_Cambio_Stagione.docx?rlkey=dgd1wtixysj33tpj1ammmfkms&dl=0";

// D369 — 7 stati semaforici con etichette e palette esatti del file v157
const STATI = {
  date_mancanti: {
    pallino: "🔴", label: "Date mancanti",
    bg: "#FCEBEB", text: "#A32D2D",
    tooltip: "Le date di inizio e fine non sono ancora state inserite. Cliccale per impostarle.",
  },
  date_inserite: {
    pallino: "🟡", label: "Date inserite, screening non attivo",
    bg: "#FAEEDA", text: "#854F0B",
    tooltip: "Le date sono impostate. Lo screening dei nomi squadra si attiverà automaticamente nei 14 giorni precedenti all'inizio del campionato.",
  },
  pronta_screening: {
    pallino: "🟢", label: "Pronta per screening",
    bg: "#EAF3DE", text: "#3B6D11",
    tooltip: "Mancano meno di 14 giorni all'inizio. Clicca 'Avvia screening' per verificare le fonti e poi avviare il controllo dei nomi squadra.",
  },
  screening_corso: {
    pallino: "🟣", label: "Screening in corso",
    bg: "#EEEDFE", text: "#3C3489",
    tooltip: "Lo screening è stato avviato ma non ancora completato. Riprendilo per finire di approvare i mapping squadra.",
  },
  screening_completato: {
    pallino: "✅", label: "Screening completato",
    bg: "#E1F5EE", text: "#0F6E56",
    tooltip: "Tutti i nomi squadra sono stati approvati. Il campionato è pronto a ricevere partite dalla pipeline.",
  },
  campionato_corso: {
    pallino: "⚪", label: "Campionato in corso",
    bg: "#F1EFE8", text: "#5F5E5A",
    tooltip: "Il campionato sta giocando: oggi è tra la data di inizio e la data di fine. La pipeline sta importando regolarmente le partite.",
  },
  campionato_terminato: {
    pallino: "⚫", label: "Campionato terminato",
    bg: "#F1EFE8", text: "#444441",
    tooltip: "Il campionato è finito: oggi è oltre la data di fine. La pipeline non importa più partite per questa stagione.",
  },
};

// Tooltip esteso per il caso d'allarme (D371)
const TOOLTIP_ALLARME =
  "⚠ Campionato già iniziato. Lo screening dei nomi squadra è stato avviato ma non concluso, e il campionato sta già giocando. La pipeline rischia di importare partite con squadre non mappate, che verranno scartate o messe in errore. Riprendi e completa lo screening il prima possibile.";

// ═══════════════════════════════════════════════════════════════
// STILI condivisi
// ═══════════════════════════════════════════════════════════════

const S = {
  pagina: {
    padding: "24px 32px",
    fontFamily: FONT,
    background: "#F8FAFC",
    minHeight: "100vh",
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    padding: "20px 24px",
    marginBottom: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    border: "1px solid #E2E8F0",
  },
  tabella: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    background: "#F1F5F9", color: "#475569", fontWeight: 600,
    padding: "9px 14px", textAlign: "left",
    borderBottom: "2px solid #E2E8F0", fontSize: 12,
  },
  td: {
    padding: "9px 14px",
    borderBottom: "1px solid #F1F5F9",
    color: "#1E293B", fontSize: 13,
  },
  tdAzioni: {
    padding: "7px 14px",
    borderBottom: "1px solid #F1F5F9",
    textAlign: "right", whiteSpace: "nowrap",
  },
  // Cella data editabile inline (D368)
  cellaData: {
    cursor: "pointer",
    textDecoration: "underline dotted #94A3B8",
    textUnderlineOffset: "3px",
    padding: "2px 4px",
    borderRadius: 4,
    display: "inline-block",
    transition: "background 0.15s",
  },
  cellaDataHover: { background: "#E6F1FB", color: "#0C447C" },
  // Pulsanti
  btnPrimario: {
    fontSize: 13, fontWeight: 600, padding: "8px 16px", borderRadius: 7,
    border: "none", background: "#185FA5", color: "#fff",
    cursor: "pointer", fontFamily: FONT,
  },
  btnOutline: {
    fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 6,
    background: "#fff", color: "#3C3489",
    border: "1px solid #AFA9EC", cursor: "pointer", fontFamily: FONT,
  },
  btnAllarme: {
    fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 6,
    background: "#C44545", color: "#fff",
    border: "none", cursor: "pointer", fontFamily: FONT,
  },
  btnNeutro: {
    fontSize: 12, fontWeight: 600, padding: "6px 14px", borderRadius: 6,
    background: "#fff", color: "#475569",
    border: "1px solid #CBD5E1", cursor: "pointer", fontFamily: FONT,
  },
  btnElimina: {
    fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 7,
    background: "#fff", color: "#C44545",
    border: "1px solid #FCA5A5", cursor: "pointer", fontFamily: FONT,
  },
  // Badge
  badgeBase: {
    fontSize: 11, fontWeight: 500, padding: "3px 9px",
    borderRadius: 10, whiteSpace: "nowrap",
    display: "inline-flex", alignItems: "center", gap: 4,
  },
};

// ═══════════════════════════════════════════════════════════════
// HELPERS — date, stagioni, calcolo stato
// ═══════════════════════════════════════════════════════════════

function isoToIt(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function oggiIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const g = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${g}`;
}

function giorniTra(isoA, isoB) {
  const a = new Date(isoA);
  const b = new Date(isoB);
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

// Stagione "25-26" → "26-27"
function stagioneSuccessiva(stagione) {
  const [a, b] = stagione.split("-").map(Number);
  return `${String(a + 1).padStart(2, "0")}-${String(b + 1).padStart(2, "0")}`;
}

// Stagione "25-26" → 1° luglio 2025 (inizio convenzionale) e 1° luglio 2026 (fine)
function inizio1LuglioStagione(stagione) {
  const a = Number(stagione.split("-")[0]); // "25"
  return `20${String(a).padStart(2, "0")}-07-01`;
}
function fine1LuglioStagione(stagione) {
  const b = Number(stagione.split("-")[1]); // "26"
  return `20${String(b).padStart(2, "0")}-07-01`;
}

// Test: data ISO oltre il 1° luglio della stagione (es. 02/07/2026 per 25-26)
function isOltre1Luglio(isoData, stagione) {
  if (!isoData) return false;
  return isoData > fine1LuglioStagione(stagione);
}
// Test: data ISO prima del 1° luglio (es. 30/06/2026 per 26-27)
function isPrima1Luglio(isoData, stagione) {
  if (!isoData) return false;
  return isoData < inizio1LuglioStagione(stagione);
}

// Stato semaforico per una lega data una riga e oggi.
// NOTA: screening_status non esiste ancora in DB (verrà in Sessione 4),
// quindi i due stati 🟣 e ✅ non sono raggiungibili da questa logica.
// La logica di "campionato in corso senza screening completato" (caso D371)
// viene gestita comunque per future estensioni quando screening_status sarà disponibile.
function calcolaStato(riga, oggi) {
  if (!riga || !riga.data_inizio || !riga.data_fine) return "date_mancanti";
  const screeningCompletato = riga.screening_status === "completato";
  const screeningInCorso = riga.screening_status === "in_corso";

  // Dopo data_fine: campionato terminato
  if (oggi > riga.data_fine) return "campionato_terminato";

  // Tra data_inizio e data_fine
  if (oggi >= riga.data_inizio && oggi <= riga.data_fine) {
    if (screeningCompletato) return "campionato_corso";
    // Caso d'allarme: campionato in corso senza screening completato
    // Manteniamo lo stato di screening (corso/pronta) per evidenza colorata + bordo allarme
    if (screeningInCorso) return "screening_corso";
    // Non c'è ancora screening_status → mostriamo campionato_corso come stato neutro.
    // L'allarme vero entrerà in Sessione 4 con screening_status reale.
    return "campionato_corso";
  }

  // Prima di data_inizio
  if (screeningCompletato) return "screening_completato";
  if (screeningInCorso) return "screening_corso";
  // Pronta per screening: meno di 14 giorni all'inizio
  const giorniAllInizio = giorniTra(oggi, riga.data_inizio);
  if (giorniAllInizio <= 14) return "pronta_screening";
  return "date_inserite";
}

// Caso d'allarme (D371): campionato già iniziato + screening esplicitamente
// AVVIATO MA NON CONCLUSO. Non scatta per screening_status null/undefined
// (= sistema non sa nulla, mapping gestiti manualmente in Impostazioni) ne'
// per "completato". L'unico scenario di allarme reale e' quando l'utente ha
// cliccato "Avvia screening", e' entrato nel Livello 2, ha lasciato a meta'
// e nel frattempo il campionato e' iniziato → rischio concreto di partite
// con squadre non mappate dalla pipeline.
function isCasoAllarme(riga, oggi) {
  if (!riga || !riga.data_inizio || !riga.data_fine) return false;
  if (oggi < riga.data_inizio || oggi > riga.data_fine) return false;
  return riga.screening_status === "in_corso";
}

// ─── Setup nuova stagione: le 4 fasi della procedura rev 2 (Blocco 1 v192) ──
// La Fase 1 non ha colonna in DB: e' derivata dalla presenza delle due date.
// Le Fasi 2/3/4 sono spuntate a mano e memorizzano la data della spunta.
const FASI = [
  { n: 1, campo: null, titolo: "Date di inizio e fine" },
  { n: 2, campo: "fase2_fatta_il", titolo: "Numero squadre" },
  { n: 3, campo: "fase3_fatta_il", titolo: "Mapping Betfair → DB" },
  { n: 4, campo: "fase4_fatta_il", titolo: "Mapping DB → SoccerSTATs" },
];

// Ritorna { fatte, fase1, dateComplete } per una riga.
function statoSetup(riga) {
  const fase1 = !!(riga && riga.data_inizio && riga.data_fine);
  let fatte = fase1 ? 1 : 0;
  FASI.forEach((f) => {
    if (f.campo && riga && riga[f.campo]) fatte += 1;
  });
  return { fatte, fase1 };
}

// Il campionato ha gia' preso il via? (setup ormai alle spalle)
function isPartito(riga, oggi) {
  return !!(riga && riga.data_inizio && oggi >= riga.data_inizio);
}

// Riga descrittiva sotto il badge: quanto manca al via.
function sottotitoloRiga(riga, oggi) {
  if (!riga || !riga.data_inizio) return "date da inserire";
  if (riga.data_fine && oggi > riga.data_fine) return "campionato terminato";
  if (oggi >= riga.data_inizio) return "campionato in corso";
  const g = giorniTra(oggi, riga.data_inizio);
  if (g === 0) return "al via oggi";
  if (g === 1) return "al via domani";
  return `al via fra ${g} giorni`;
}

// Stato a livello stagione (per il dropdown)
function statoStagione(righe, oggi) {
  if (righe.length === 0) return "in preparazione";
  const tutteIniziate = righe.every(
    (r) => r.data_inizio && r.data_inizio <= oggi
  );
  const tutteFinite = righe.every(
    (r) => r.data_fine && r.data_fine < oggi
  );
  if (tutteFinite) return "conclusa";
  // D364: "in corso" se almeno un campionato sta giocando
  const almenoUnaInCorso = righe.some(
    (r) =>
      r.data_inizio &&
      r.data_fine &&
      r.data_inizio <= oggi &&
      r.data_fine >= oggi
  );
  if (almenoUnaInCorso) return "in corso";
  // Se ci sono date ma nessuna lega ha ancora iniziato
  const qualcunaConDate = righe.some((r) => r.data_inizio);
  if (qualcunaConDate && !tutteIniziate) return "in preparazione";
  return "in preparazione";
}

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTI
// ═══════════════════════════════════════════════════════════════

// ─── Badge di stato con tooltip dark (D369 + D370 + D371) ───────
// v197: intestazione di colonna cliccabile per ordinare la tabella leghe.
const SUGGERIMENTI_ORDINE = {
  lega: "Ordina per nome del campionato",
  data_inizio: "Ordina per data di inizio",
  data_fine: "Ordina per data di fine",
  stato: "Ordina per avanzamento del setup: i meno completi in cima",
};

function ThOrd({ col, attiva, onClick, children }) {
  const isAttiva = attiva.col === col;
  const freccia = !isAttiva ? "" : attiva.dir === "asc" ? " ▲" : " ▼";
  return (
    <th
      style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
      onClick={() => onClick(col)}
      title={SUGGERIMENTI_ORDINE[col]}
    >
      <span style={{ color: isAttiva ? "#1D4ED8" : "#475569" }}>
        {children}{freccia}
      </span>
    </th>
  );
}

function BadgeStato({ statoKey, allarme }) {
  const [hover, setHover] = useState(false);
  const def = STATI[statoKey];
  if (!def) return null;
  const tooltip = allarme ? TOOLTIP_ALLARME : def.tooltip;
  return (
    <span
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span
        style={{
          ...S.badgeBase,
          background: def.bg,
          color: def.text,
          border: allarme ? "1px solid #C44545" : "none",
        }}
      >
        <span>{def.pallino}</span>
        <span>{def.label}</span>
        {allarme && <span style={{ color: "#A32D2D", marginLeft: 2 }}>⚠</span>}
      </span>
      {hover && (
        <span
          style={{
            position: "absolute",
            left: 0,
            top: "130%",
            background: "#24211D",
            color: "#fff",
            padding: "8px 11px",
            borderRadius: 6,
            fontSize: 11,
            lineHeight: 1.5,
            width: allarme ? 280 : 240,
            zIndex: 50,
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            fontWeight: 400,
          }}
        >
          {tooltip}
        </span>
      )}
    </span>
  );
}

// ─── Badge avanzamento setup "N di 4" (Blocco 1 v192) ───────────
function BadgeSetup({ fatte, prefisso, allarme }) {
  const [hover, setHover] = useState(false);
  const completo = fatte === 4;
  const vuoto = fatte === 0;
  // allarme: il campionato e' gia' partito con il setup incompleto (v192)
  const bg = allarme ? "#FCEBEB" : completo ? "#EAF3DE" : vuoto ? "#FCEBEB" : "#FAEEDA";
  const text = allarme ? "#A32D2D" : completo ? "#3B6D11" : vuoto ? "#A32D2D" : "#854F0B";
  const tooltip = allarme
    ? `Il campionato ha gia' preso il via ma il setup non e' completo: ${fatte} fasi su 4. Apri il dettaglio e completa quelle che mancano: finche' restano aperte, i dati importati per questo campionato possono essere sbagliati.`
    : completo
    ? "Tutte e quattro le fasi del setup risultano completate per questo campionato."
    : `Sono state completate ${fatte} fasi del setup su 4. Apri il dettaglio per vedere quali mancano.`;
  return (
    <span
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span style={{
        ...S.badgeBase, background: bg, color: text,
        border: allarme ? "1px solid #C44545" : "none",
      }}>
        {allarme && <span style={{ marginRight: 2 }}>⚠</span>}
        {prefisso ? `${prefisso} · ` : ""}{fatte} di 4
        {completo && !prefisso ? " · completo" : ""}
      </span>
      {hover && (
        <span
          style={{
            position: "absolute", left: 0, top: "130%",
            background: "#24211D", color: "#fff",
            padding: "8px 11px", borderRadius: 6,
            fontSize: 11, lineHeight: 1.5, width: 240,
            zIndex: 50, boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            fontWeight: 400,
          }}
        >
          {tooltip}
        </span>
      )}
    </span>
  );
}

// ─── Link procedura modificabile (Blocco 1 v192) ────────────────
function LinkProcedura({ etichetta, valore, onSalva, compatto, stilePulsante }) {
  const [editing, setEditing] = useState(false);
  const [tmp, setTmp] = useState(valore || "");

  useEffect(() => { setTmp(valore || ""); }, [valore]);

  if (editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <input
          value={tmp}
          autoFocus
          placeholder="Incolla qui l'indirizzo Dropbox del documento"
          onChange={(e) => setTmp(e.target.value)}
          style={{
            fontSize: 12, padding: "5px 9px", width: 380,
            border: "1px solid #CBD5E1", borderRadius: 6, fontFamily: FONT,
          }}
        />
        <button
          onClick={() => { onSalva(tmp.trim() || null); setEditing(false); }}
          style={{ ...S.btnNeutro, padding: "5px 12px" }}
        >Salva</button>
        <button
          onClick={() => { setTmp(valore || ""); setEditing(false); }}
          style={{ ...S.btnNeutro, padding: "5px 12px", color: "#94A3B8" }}
        >Annulla</button>
      </span>
    );
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
      {!compatto && <span style={{ fontSize: 13 }}>📄</span>}
      {valore ? (
        <a
          href={valore} target="_blank" rel="noopener noreferrer"
          style={stilePulsante || { fontSize: 12, fontWeight: 600, color: "#185FA5" }}
        >{etichetta}</a>
      ) : (
        <span style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic" }}>
          {etichetta} — nessun indirizzo salvato
        </span>
      )}
      <button
        onClick={() => setEditing(true)}
        title="Modifica l'indirizzo del documento"
        style={{
          fontSize: 11, lineHeight: 1, padding: "3px 6px", borderRadius: 5,
          background: "#fff", border: "1px solid #CBD5E1",
          cursor: "pointer", fontFamily: FONT,
        }}
      >✏️</button>
    </span>
  );
}

// ─── Pannello dettaglio delle 4 fasi + nota (Blocco 1 v192) ─────
function DettaglioSetup({ riga, onToggleFase, onSalvaNota }) {
  const [nota, setNota] = useState(riga.note_setup || "");
  const [notaSalvata, setNotaSalvata] = useState(false);

  useEffect(() => { setNota(riga.note_setup || ""); }, [riga.note_setup]);

  const fase1Ok = !!(riga.data_inizio && riga.data_fine);
  // v192: a campionato finito le fasi diventano di sola lettura.
  // Non c'e' piu' nulla da completare, e riaprire una casella produrrebbe
  // solo un allarme su una stagione archiviata. La nota resta scrivibile.
  const soloLettura = !!(riga.data_fine && oggiIso() > riga.data_fine);

  return (
    <div style={{ padding: "14px 18px 16px", background: "#F8FAFC" }}>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>
        {riga.lega} · le quattro fasi del setup
        {soloLettura && (
          <span style={{ marginLeft: 8, color: "#94A3B8", fontStyle: "italic" }}>
            campionato concluso · sola lettura
          </span>
        )}
      </div>

      {FASI.map((f) => {
        const auto = f.campo === null;
        const fatta = auto ? fase1Ok : !!riga[f.campo];
        return (
          <div
            key={f.n}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 0", borderTop: "1px solid #E2E8F0",
            }}
          >
            <span style={{
              fontSize: 15, fontWeight: 700,
              color: fatta ? "#3B6D11" : "#C77700",
            }}>
              {fatta ? "✔" : "✕"}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "#1E293B" }}>
                {f.n} · {f.titolo}
              </div>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                {auto
                  ? (fatta
                      ? "spuntata da sola: le due date sono compilate"
                      : "si spunta da sola quando inserisci inizio e fine")
                  : (fatta
                      ? `segnata fatta il ${isoToIt(riga[f.campo])}`
                      : "da fare")}
              </div>
            </div>
            {!auto && !soloLettura && (
              <button
                onClick={() => onToggleFase(riga.lega, f.campo, !fatta)}
                style={fatta
                  ? { ...S.btnNeutro, padding: "4px 10px", fontSize: 11 }
                  : { ...S.btnPrimario, padding: "5px 12px", fontSize: 11 }}
              >{fatta ? "Annulla" : "Segna fatta"}</button>
            )}
          </div>
        );
      })}

      {/* Nota libera del campionato */}
      <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 12, marginTop: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
          Nota di lavoro
        </div>
        <textarea
          value={nota}
          onChange={(e) => { setNota(e.target.value); setNotaSalvata(false); }}
          placeholder="Appunti su questo campionato mentre completi il setup…"
          rows={3}
          style={{
            width: "100%", boxSizing: "border-box",
            fontSize: 12, lineHeight: 1.5, padding: "8px 10px",
            border: "1px solid #CBD5E1", borderRadius: 6,
            fontFamily: FONT, resize: "vertical", background: "#fff",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <button
            onClick={async () => {
              await onSalvaNota(riga.lega, nota.trim() || null);
              setNotaSalvata(true);
            }}
            style={{ ...S.btnPrimario, padding: "6px 14px", fontSize: 12 }}
          >Salva nota</button>
          <button
            onClick={async () => {
              setNota("");
              setNotaSalvata(false);
              await onSalvaNota(riga.lega, null);
            }}
            style={{ ...S.btnNeutro, padding: "6px 14px", fontSize: 12 }}
          >Cancella nota</button>
          {notaSalvata && (
            <span style={{ fontSize: 11, color: "#3B6D11" }}>Nota salvata.</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Cella data editabile inline (D368) ─────────────────────────
// ─── Avviso sulle date di un campionato concluso (v194, D651) ───
function AvvisoDataConclusa({ conclusoIl, campo }) {
  return (
    <div style={{
      marginTop: 8, maxWidth: 460,
      background: "#FAEEDA", borderLeft: "3px solid #EF9F27",
      padding: "10px 13px", textAlign: "left",
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#633806", marginBottom: 4 }}>
        ⚠ Campionato concluso il {isoToIt(conclusoIl)}
      </div>
      <div style={{ fontSize: 11.5, color: "#854F0B", lineHeight: 1.65 }}>
        {campo === "fine" ? (
          <>
            Correggere la data non cambia le partite già nel database.<br />
            Se porti la fine oltre oggi, il campionato torna in corso e tornano
            modificabili le spunte delle altre fasi del setup: numero squadre,
            mapping Betfair → DB e mapping DB → SoccerSTATs.
          </>
        ) : (
          <>
            Cambiare la data di inizio non tocca le partite già nel database:
            dice solo da quando il campionato è partito.<br />
            Se la cancelli, la prima fase del setup di inizio stagione — date di
            inizio e fine — torna da fare.
          </>
        )}
      </div>
    </div>
  );
}

// v194 (D651): a campionato concluso le date restano modificabili, ma aprendo
// il campo compare un avviso che dice cosa comporta davvero la correzione.
// Nessun blocco: il rifiuto non serve perche' nessun dato gia' a database si muove.
// conclusoIl = data_fine ISO se il campionato e' terminato, altrimenti null.
// campo = "inizio" | "fine" (decide quale dei due testi mostrare).
function CellaData({ valore, onSalva, conclusoIl = null, campo = "inizio" }) {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const [tmp, setTmp] = useState(valore || "");

  useEffect(() => {
    setTmp(valore || "");
  }, [valore]);

  if (editing) {
    return (
      <span style={{ display: "inline-block" }}>
      <span style={{ display: "inline-flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
        <input
          type="date"
          value={tmp}
          autoFocus
          onChange={(e) => setTmp(e.target.value)}
          style={{
            fontSize: 12,
            padding: "3px 6px",
            border: "1px solid #185FA5",
            borderRadius: 4,
            fontFamily: FONT,
          }}
        />
        <button
          onClick={() => {
            // Salva intelligente:
            // - se tmp e' vuoto e c'era un valore → equivale a cancellazione (invia null)
            // - se tmp e' valorizzato e diverso da valore → salva nuova data
            // - se tmp uguale a valore → no-op
            if (tmp === "" && valore) {
              onSalva(null);
            } else if (tmp && tmp !== valore) {
              onSalva(tmp);
            }
            setEditing(false);
          }}
          style={{
            fontSize: 11, fontWeight: 600,
            padding: "3px 8px", borderRadius: 4,
            background: "#16A34A", color: "#fff",
            border: "none", cursor: "pointer",
          }}
        >Salva</button>
        {valore && (
          <button
            onClick={() => {
              onSalva(null);
              setEditing(false);
            }}
            style={{
              fontSize: 11, fontWeight: 600,
              padding: "3px 8px", borderRadius: 4,
              background: "#fff", color: "#C44545",
              border: "1px solid #FCA5A5", cursor: "pointer",
            }}
            title="Cancella data (torna a Date mancanti)"
          >🗑 Cancella</button>
        )}
        <button
          onClick={() => {
            setTmp(valore || "");
            setEditing(false);
          }}
          style={{
            fontSize: 11, fontWeight: 600,
            padding: "3px 8px", borderRadius: 4,
            background: "#fff", color: "#64748B",
            border: "1px solid #CBD5E1", cursor: "pointer",
          }}
        >Annulla</button>
      </span>
      {conclusoIl && <AvvisoDataConclusa conclusoIl={conclusoIl} campo={campo} />}
      </span>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...S.cellaData,
        ...(hover ? S.cellaDataHover : {}),
      }}
      title="Clicca per modificare"
    >
      {isoToIt(valore)}
    </span>
  );
}

// ─── Tooltip "(?)" stati delle stagioni (D363) ──────────────────
function TooltipStati() {
  const [aperto, setAperto] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <span
        onClick={() => setAperto((a) => !a)}
        onMouseEnter={() => setAperto(true)}
        onMouseLeave={() => setAperto(false)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16, height: 16, borderRadius: "50%",
          border: "1px solid #94A3B8",
          background: "#fff",
          color: "#64748B",
          fontSize: 11, fontWeight: 600,
          cursor: "pointer",
          marginLeft: 6,
        }}
      >?</span>
      {aperto && (
        <span
          style={{
            position: "absolute",
            left: "120%", top: "-4px",
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: 8,
            padding: "10px 14px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            width: 360,
            zIndex: 50,
            fontSize: 12, lineHeight: 1.6,
            color: "#1E293B",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Stati delle stagioni:</div>
          <div><strong>in preparazione</strong> — esiste in tabella ma nessun campionato è ancora iniziato</div>
          <div><strong>in corso</strong> — almeno un campionato sta giocando</div>
          <div><strong>conclusa</strong> — tutti i campionati hanno superato la propria data di fine</div>
        </span>
      )}
    </span>
  );
}

// ─── Modale conferma "+ Aggiungi nuova stagione" (D365) ─────────
function ModaleConfermaAggiungi({ corpo, onConferma, onAnnulla }) {
  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(36,33,29,0.45)",
      zIndex: 100,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 12,
        maxWidth: 460, width: "90%",
        padding: "22px 24px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
        fontFamily: FONT,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
          <span style={{
            fontSize: 22, lineHeight: 1, color: "#185FA5",
          }}>ⓘ</span>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1E293B" }}>
            Confermi la creazione della nuova stagione?
          </div>
        </div>
        <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, marginBottom: 18 }}>
          {corpo}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onAnnulla} style={S.btnNeutro}>Annulla</button>
          <button onClick={onConferma} style={S.btnPrimario}>Procedi</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modale conferma "🗑 Elimina stagione" ──────────────────────
function ModaleConfermaElimina({ stagione, onConferma, onAnnulla }) {
  const [eliminando, setEliminando] = useState(false);
  const procedi = async () => {
    setEliminando(true);
    await onConferma();
    setEliminando(false);
  };
  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(36,33,29,0.45)",
      zIndex: 100,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 12,
        maxWidth: 460, width: "90%",
        padding: "22px 24px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
        fontFamily: FONT,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
          <span style={{ fontSize: 22, lineHeight: 1, color: "#C44545" }}>⚠</span>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1E293B" }}>
            Eliminare la stagione {stagione}?
          </div>
        </div>
        <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, marginBottom: 18 }}>
          Tutte le 11 righe della stagione <strong>{stagione}</strong> verranno rimosse dal database.
          L'operazione è permessa perché nessun campionato ha date inserite, quindi non si perdono dati operativi.
          Vuoi procedere?
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onAnnulla} disabled={eliminando} style={S.btnNeutro}>Annulla</button>
          <button
            onClick={procedi}
            disabled={eliminando}
            style={{
              fontSize: 13, fontWeight: 600, padding: "8px 16px", borderRadius: 7,
              border: "none", background: "#C44545", color: "#fff",
              cursor: eliminando ? "not-allowed" : "pointer", fontFamily: FONT,
            }}
          >{eliminando ? "Eliminazione…" : "Elimina"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Avviso bidirezionale (D360-D361) ───────────────────────────
function AvvisoBidirezionale({ lega, tipo, isoData, stagione, dataSuggerita, onAttivaForzatura }) {
  const dataLimite = tipo === "oltre"
    ? `oltre il 1° luglio`
    : `prima del 1° luglio`;
  const dataFmt = isoToIt(isoData);
  const sugg = isoToIt(dataSuggerita);
  const stagAttribuita = tipo === "oltre" ? stagioneSuccessiva(stagione) : prevStagione(stagione);
  const portaInStag = tipo === "oltre" ? stagione : stagioneSuccessiva(stagione);
  const messaggio = tipo === "oltre"
    ? `${lega}: fine prevista il ${dataFmt}, ${dataLimite}. Le partite dopo quella soglia verrebbero attribuite alla stagione ${stagAttribuita}. Per tenerle nella ${portaInStag} imposta la forzatura al ${sugg}.`
    : `${lega}: inizio previsto il ${dataFmt}, ${dataLimite}. Le partite prima di quella soglia verrebbero attribuite alla stagione ${stagAttribuita}. Per portarle nella ${portaInStag} imposta la forzatura al ${sugg}.`;
  return (
    <div style={{
      background: "#FCEBEB",
      border: "1px solid #A32D2D",
      borderRadius: 8,
      padding: "12px 16px",
      marginTop: 12,
      display: "flex",
      alignItems: "flex-start", gap: 10,
      fontFamily: FONT,
    }}>
      <span style={{ fontSize: 18, color: "#A32D2D", flexShrink: 0, lineHeight: 1 }}>⚠</span>
      <div style={{ fontSize: 12, lineHeight: 1.6, color: "#791F1F" }}>
        {messaggio}{" "}
        <span
          onClick={() => onAttivaForzatura(dataSuggerita, lega)}
          style={{
            cursor: "pointer", textDecoration: "underline",
            fontWeight: 700, color: "#791F1F", whiteSpace: "nowrap",
          }}
        >Attiva forzatura ora →</span>
      </div>
    </div>
  );
}
function prevStagione(stagione) {
  const [a, b] = stagione.split("-").map(Number);
  return `${String(a - 1).padStart(2, "0")}-${String(b - 1).padStart(2, "0")}`;
}

// ─── Avviso aggregato caso d'allarme (D371) ─────────────────────
function AvvisoAllarmeAggregato({ legheAllarme }) {
  return (
    <div style={{
      background: "#FCEBEB",
      border: "1px solid #A32D2D",
      borderRadius: 8,
      padding: "12px 16px",
      marginTop: 14,
      display: "flex",
      alignItems: "flex-start", gap: 10,
      fontFamily: FONT,
    }}>
      <span style={{ fontSize: 18, color: "#A32D2D", flexShrink: 0, lineHeight: 1 }}>⚠</span>
      <div style={{ fontSize: 12, lineHeight: 1.6, color: "#791F1F" }}>
        <div style={{ marginBottom: 6 }}>
          <strong>{legheAllarme.length}</strong> campionati sono già iniziati ma hanno uno screening avviato e non concluso.
          Le partite in arrivo per queste leghe potrebbero non venire associate correttamente alle squadre nel database
          e finire scartate o in errore. Riprendi e completa lo screening il prima possibile.
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 6 }}>
          {legheAllarme.map((l) => (
            <span key={l} style={{
              textDecoration: "underline", fontWeight: 600, color: "#791F1F",
            }}>{l} →</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Riquadro "Termine stagione sportiva" (D359 + TASK 17-bis v164) ────
//
// 2 modalita':
// - **modificabile** (default): tasti attivi (Attiva/Aggiorna/Disattiva forzatura),
//   testo descrittivo unico parametrizzato sulla stagione visualizzata.
// - **archivio** (prop archivio=true): tasti disabilitati, banner "Non modificabile"
//   in alto, testo storico che racconta come la stagione e' terminata (con o senza
//   forzatura). Riquadro tratteggiato per indicare visualmente la natura read-only.
//
function RiquadroTermineStagione({
  stagioneVisualizzata, archivio,
  forzaturaAttiva, dataForzatura, dataPrecompilata, legaSuggerita,
  onAttiva, onAggiorna, onDisattiva, riquadroRef,
}) {
  const [tmpData, setTmpData] = useState(dataPrecompilata || dataForzatura || "");
  useEffect(() => {
    if (dataPrecompilata) setTmpData(dataPrecompilata);
  }, [dataPrecompilata]);
  useEffect(() => {
    if (!dataPrecompilata) setTmpData(dataForzatura || "");
  }, [dataForzatura, dataPrecompilata]);

  // Anno della data di default (1° luglio di quale anno) — es. stagione "25-26" → 2026
  const annoFineDefault = stagioneVisualizzata
    ? `20${stagioneVisualizzata.split("-")[1]}`
    : "";

  // ─── Palette per modalita' ────────────────────────────────────
  let cardBg, cardBorder, cardBorderStyle = "solid", colTitolo, colTesto;
  if (archivio) {
    cardBg = "#FAFBFC";
    cardBorder = "#94A3B8";
    cardBorderStyle = "dashed";
    colTitolo = "#475569";
    colTesto = "#64748B";
  } else if (forzaturaAttiva) {
    cardBg = "#FEF6E7";
    cardBorder = "#BA7517";
    colTitolo = "#542F03";
    colTesto = "#633806";
  } else {
    cardBg = "#F1F5F9";
    cardBorder = "#CBD5E1";
    colTitolo = "#1E293B";
    colTesto = "#475569";
  }

  // ─── Badge stato accanto al titolo ────────────────────────────
  let badgeText;
  if (archivio && !forzaturaAttiva) {
    badgeText = "nessuna forzatura impostata";
  } else if (forzaturaAttiva) {
    badgeText = `forzatura attiva: ${isoToIt(dataForzatura)}`;
  } else {
    badgeText = "forzatura non attiva";
  }
  const badgeBg = forzaturaAttiva ? "#FAEEDA" : "#fff";
  const badgeColor = forzaturaAttiva ? "#854F0B" : "#64748B";
  const badgeBorder = forzaturaAttiva ? "none" : "1px solid #CBD5E1";

  // ─── Testo descrittivo ────────────────────────────────────────
  let testoDescrittivo;
  if (archivio) {
    if (forzaturaAttiva) {
      testoDescrittivo = `La stagione ${stagioneVisualizzata} è terminata il ${isoToIt(dataForzatura)} (data forzata in fase operativa). Questa è la storia conservata della forzatura impostata all'epoca.`;
    } else {
      testoDescrittivo = `Nessuna forzatura era stata impostata. La stagione ${stagioneVisualizzata} è terminata alla data di default 1° luglio ${annoFineDefault}.`;
    }
  } else {
    testoDescrittivo = `La stagione ${stagioneVisualizzata} terminerà quando tutti i campionati elencati saranno finiti. Le attività automatiche di import eventi saranno sospese dopo 7 giorni da tale data (margine per partite recuperate o risultati in ritardo) e riprenderanno all'avvio della stagione successiva. La data di fine stagione è fissata, di default, al 1° luglio ${annoFineDefault}, cambiala da qui se lo ritieni necessario.`;
  }

  // ─── Banner read-only (solo in archivio) ──────────────────────
  const bannerReadOnly = archivio ? (
    <div style={{
      background: "#F1F5F9", border: "1px solid #CBD5E1",
      borderRadius: 6, padding: "8px 12px", marginBottom: 12,
      fontSize: 12, color: "#475569", lineHeight: 1.5,
      display: "flex", alignItems: "flex-start", gap: 8,
    }}>
      <span style={{ flexShrink: 0 }}>🔒</span>
      <span>
        Non modificabile: la stagione <strong>{stagioneSuccessiva(stagioneVisualizzata || "")}</strong> è già stata aggiunta.
      </span>
    </div>
  ) : null;

  // ─── Stili tasti disabilitati ────────────────────────────────
  const btnDisabilitato = {
    ...S.btnNeutro,
    background: "#F1F5F9",
    color: "#94A3B8",
    borderColor: "#E2E8F0",
    cursor: "not-allowed",
  };

  return (
    <div ref={riquadroRef} style={{
      background: cardBg,
      border: `1px ${cardBorderStyle} ${cardBorder}`,
      borderRadius: 8,
      padding: "14px 18px",
      marginBottom: 16,
      fontFamily: FONT,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 16, color: forzaturaAttiva ? "#854F0B" : "#64748B" }}>📅</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: colTitolo }}>
          Termine stagione sportiva
        </span>
        <span style={{
          fontSize: 11, fontWeight: 600,
          padding: "2px 8px", borderRadius: 8,
          background: badgeBg, color: badgeColor, border: badgeBorder,
        }}>
          {badgeText}
        </span>
      </div>

      {bannerReadOnly}

      <div style={{ fontSize: 12, lineHeight: 1.6, color: colTesto, marginBottom: 10 }}>
        {testoDescrittivo}
      </div>

      {legaSuggerita && !archivio && (
        <div style={{ fontSize: 11, color: "#854F0B", marginBottom: 8, fontStyle: "italic" }}>
          suggerito dall'avviso su {legaSuggerita}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input
          type="date"
          value={tmpData}
          onChange={(e) => setTmpData(e.target.value)}
          disabled={archivio}
          placeholder="gg/mm/aaaa"
          style={{
            fontSize: 12, padding: "6px 10px",
            border: "1px solid #CBD5E1", borderRadius: 6,
            fontFamily: FONT,
            background: archivio ? "#F1F5F9" : "#fff",
            color: archivio ? "#94A3B8" : "#1E293B",
            cursor: archivio ? "not-allowed" : "auto",
          }}
        />
        {!forzaturaAttiva ? (
          <button
            onClick={() => !archivio && tmpData && onAttiva(tmpData)}
            disabled={archivio || !tmpData}
            style={
              archivio
                ? btnDisabilitato
                : {
                    ...S.btnNeutro,
                    borderColor: tmpData ? "#185FA5" : "#CBD5E1",
                    color: tmpData ? "#185FA5" : "#94A3B8",
                    cursor: tmpData ? "pointer" : "not-allowed",
                  }
            }
            title={
              archivio
                ? "Non modificabile: la stagione successiva è già stata aggiunta"
                : !tmpData
                ? "Inserisci una data nel campo per attivare la forzatura"
                : "Attiva la forzatura"
            }
          >Attiva forzatura</button>
        ) : (
          <>
            <button
              onClick={() => {
                if (!archivio && tmpData && tmpData !== dataForzatura) onAggiorna(tmpData);
              }}
              disabled={archivio || !tmpData || tmpData === dataForzatura}
              style={
                archivio
                  ? btnDisabilitato
                  : {
                      ...S.btnNeutro,
                      borderColor: (tmpData && tmpData !== dataForzatura) ? "#185FA5" : "#CBD5E1",
                      color: (tmpData && tmpData !== dataForzatura) ? "#185FA5" : "#94A3B8",
                      cursor: (tmpData && tmpData !== dataForzatura) ? "pointer" : "not-allowed",
                    }
              }
              title={
                archivio
                  ? "Non modificabile: la stagione successiva è già stata aggiunta"
                  : tmpData === dataForzatura
                  ? "La data nel campo coincide con quella attualmente in forza"
                  : !tmpData
                  ? "Inserisci una data nel campo per aggiornare"
                  : "Aggiorna la data della forzatura"
              }
            >Aggiorna data</button>
            <button
              onClick={() => !archivio && onDisattiva()}
              disabled={archivio}
              style={archivio ? btnDisabilitato : S.btnNeutro}
              title={
                archivio
                  ? "Non modificabile: la stagione successiva è già stata aggiunta"
                  : "Disattiva la forzatura"
              }
            >
              Disattiva forzatura
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Riquadro Investisport Quote (D366-D367) ────────────────────
function RiquadroInvestisportQuote({
  stato, stagioneCorrente, primoMatchData, primoMatchLega,
  linkProcedura, onSalvaLink, onRicontrolla, ricontrolloInCorso,
}) {
  // stato: "azione_richiesta" | "azione_eseguita" | "non_verificabile" | "assente"
  if (stato === "assente") return null;

  // Palette per stato (i due stati storici mantengono i colori del file v157)
  const P = {
    azione_eseguita: {
      bg: "#F0F8F4", bordo: "#4A9B7C", titolo: "#0F6E56", testo: "#0F6E56",
      badgeBg: "#E1F5EE", badgeTesto: "#0F6E56", etichetta: "✓ azione eseguita",
      btnBg: "#fff", btnTesto: "#0F6E56", btnBordo: "1px solid #4A9B7C",
    },
    azione_richiesta: {
      bg: "#FEF6E7", bordo: "#BA7517", titolo: "#542F03", testo: "#633806",
      badgeBg: "#FAEEDA", badgeTesto: "#854F0B", etichetta: "azione richiesta",
      btnBg: "#BA7517", btnTesto: "#fff", btnBordo: "none",
    },
    non_verificabile: {
      bg: "#F8FAFC", bordo: "#CBD5E1", titolo: "#334155", testo: "#475569",
      badgeBg: "#E2E8F0", badgeTesto: "#475569", etichetta: "non verificabile",
      btnBg: "#fff", btnTesto: "#334155", btnBordo: "1px solid #CBD5E1",
    },
  };
  const p = P[stato] || P.azione_richiesta;
  const stagionePrec = prevStagione(stagioneCorrente);

  return (
    <div style={{
      background: p.bg,
      border: `1px solid ${p.bordo}`,
      borderRadius: 8,
      padding: "14px 18px",
      fontFamily: FONT,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 18, color: p.titolo }}>🗃</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: p.titolo }}>
          Investisport Quote
        </span>
        <span style={{
          fontSize: 11, fontWeight: 600,
          padding: "2px 8px", borderRadius: 8,
          background: p.badgeBg, color: p.badgeTesto,
        }}>
          {p.etichetta}
        </span>
      </div>

      <div style={{ fontSize: 12, lineHeight: 1.6, color: p.testo, marginBottom: 12 }}>
        {stato === "azione_eseguita" && (
          <>Il sistema di download delle quote è allineato alla stagione {stagioneCorrente}. Nessuna azione richiesta.</>
        )}
        {stato === "azione_richiesta" && (
          <>
            Il sistema di download delle quote è ancora sulla stagione <strong>{stagionePrec}</strong>,
            mentre hai appena creato la <strong>{stagioneCorrente}</strong>.
            {primoMatchData ? (
              <> Aggiorna la stagione, seguendo l'apposita procedura, entro il <strong>{isoToIt(primoMatchData)}</strong>, primo match della nuova stagione ({primoMatchLega}).</>
            ) : (
              <> Aggiorna la stagione seguendo l'apposita procedura.</>
            )}
          </>
        )}
        {stato === "non_verificabile" && (
          <>
            Non è stato possibile leggere l'archivio delle quote, quindi al momento non si sa
            a quale stagione sia allineato. <strong>Non vuol dire che sia rimasto indietro</strong>:
            vuol dire che il controllo non è riuscito. Riprova fra qualche minuto.
          </>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {stato === "non_verificabile" && (
          <button
            onClick={onRicontrolla}
            disabled={ricontrolloInCorso}
            style={{
              fontSize: 12, fontWeight: 600,
              padding: "7px 14px", borderRadius: 6,
              background: p.btnBg, color: p.btnTesto,
              border: p.btnBordo, fontFamily: FONT,
              cursor: ricontrolloInCorso ? "default" : "pointer",
              opacity: ricontrolloInCorso ? 0.6 : 1,
            }}
          >{ricontrolloInCorso ? "Controllo in corso…" : "Controlla di nuovo"}</button>
        )}
        <LinkProcedura
          etichetta="↗ Apri procedura"
          valore={linkProcedura}
          onSalva={onSalvaLink}
          compatto
          stilePulsante={{
            display: "inline-block",
            fontSize: 12, fontWeight: 600,
            padding: "7px 14px", borderRadius: 6,
            background: p.btnBg, color: p.btnTesto,
            border: p.btnBordo, textDecoration: "none",
            fontFamily: FONT,
          }}
        />
      </div>
    </div>
  );
}

// ─── Toast informativo ──────────────────────────────────────────
function Toast({ messaggio, onChiudi }) {
  useEffect(() => {
    if (!messaggio) return;
    const t = setTimeout(onChiudi, 6000);
    return () => clearTimeout(t);
  }, [messaggio, onChiudi]);
  if (!messaggio) return null;
  return (
    <div style={{
      position: "fixed",
      bottom: 24, right: 24,
      background: "#1E293B",
      color: "#fff",
      padding: "12px 18px",
      borderRadius: 8,
      boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
      maxWidth: 380,
      fontSize: 12, lineHeight: 1.5,
      fontFamily: FONT,
      zIndex: 200,
    }}>
      {messaggio}
      <span
        onClick={onChiudi}
        style={{ marginLeft: 12, cursor: "pointer", fontWeight: 700 }}
      >×</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PAGINA PRINCIPALE
// ═══════════════════════════════════════════════════════════════

export default function SetupNuovaStagione() {
  const [righe, setRighe] = useState([]);
  const [impostazioni, setImpostazioni] = useState({
    override_stagione: null,
    override_data_fine: null,
  });
  // TASK 17-bis v164: forzature per stagione (sostituisce override_data_fine globale)
  // Mappa stagione → { override_data_fine: "yyyy-mm-dd" | null }
  const [stagioniMeta, setStagioniMeta] = useState({});
  const [quoteStatus, setQuoteStatus] = useState(null);
  const [stagioneSelezionata, setStagioneSelezionata] = useState(null);
  const [caricamento, setCaricamento] = useState(true);

  const [modaleAggiungi, setModaleAggiungi] = useState(false);
  const [modaleElimina, setModaleElimina] = useState(false);
  // Blocco 1 v192: lega col dettaglio delle 4 fasi aperto (una alla volta)
  const [dettaglioAperto, setDettaglioAperto] = useState(null);
  const [ricontrolloQuote, setRicontrolloQuote] = useState(false);
  const [toast, setToast] = useState("");
  const [dataPrecompilataForzatura, setDataPrecompilataForzatura] = useState("");
  const [legaSuggerimentoForzatura, setLegaSuggerimentoForzatura] = useState("");

  const riquadroForzaturaRef = useRef(null);

  const oggi = oggiIso();

  // ─── Caricamento dati iniziali ────────────────────────────────
  const caricaTutto = async () => {
    setCaricamento(true);
    try {
      const [stLeghe, imp, qs, stMeta] = await Promise.all([
        fetch(`${API}/impostazioni/stagioni-leghe`).then((r) => r.json()),
        fetch(`${API}/impostazioni/utente`).then((r) => r.json()),
        fetch(`${API}/impostazioni/investisport-quote-status`).then((r) => r.json()).catch(() => null),
        // TASK 17-bis v164: carica forzature per stagione
        fetch(`${API}/impostazioni/stagioni-meta`).then((r) => r.json()).catch(() => []),
      ]);
      setRighe(stLeghe || []);
      setImpostazioni(imp || {});
      setQuoteStatus(qs);
      // Trasforma array stagioni_meta in mappa per accesso rapido per stagione
      const metaMap = {};
      (stMeta || []).forEach((m) => { metaMap[m.stagione] = m; });
      setStagioniMeta(metaMap);
      // Seleziona la stagione più recente di default
      if (stLeghe && stLeghe.length > 0) {
        const stagioniDisp = [...new Set(stLeghe.map((r) => r.stagione))].sort().reverse();
        setStagioneSelezionata((prev) => prev || stagioniDisp[0]);
      }
    } catch (e) {
      console.error("Errore caricamento:", e);
    }
    setCaricamento(false);
  };

  useEffect(() => { caricaTutto(); }, []);

  // Blocco 1 v192: ricarica solo le righe, senza schermata "Caricamento…".
  // Usata dalle spunte e dalle note, che non devono far sfarfallare la pagina.
  const ricaricaRighe = async () => {
    try {
      const r = await fetch(`${API}/impostazioni/stagioni-leghe`).then((x) => x.json());
      setRighe(r || []);
    } catch (e) {
      console.error("Errore ricarica righe:", e);
    }
  };

  // Blocco 1 v192: rilancia il solo controllo dell'archivio quote.
  const ricontrollaQuote = async () => {
    setRicontrolloQuote(true);
    try {
      const qs = await fetch(`${API}/impostazioni/investisport-quote-status`)
        .then((r) => r.json())
        .catch(() => null);
      setQuoteStatus(qs);
    } catch (e) {
      console.error("Errore ricontrollo quote:", e);
    }
    setRicontrolloQuote(false);
  };

  // ─── Stagioni disponibili nel dropdown ────────────────────────
  const stagioniDisponibili = useMemo(() => {
    const set = new Set(righe.map((r) => r.stagione));
    return [...set].sort().reverse();
  }, [righe]);

  // ─── Righe della stagione selezionata, allineate alle 11 leghe ─
  const righeStagione = useMemo(() => {
    if (!stagioneSelezionata) return [];
    const map = {};
    righe.filter((r) => r.stagione === stagioneSelezionata).forEach((r) => { map[r.lega] = r; });
    return LEGHE.map((lega) => map[lega] || { lega, stagione: stagioneSelezionata, data_inizio: null, data_fine: null });
  }, [righe, stagioneSelezionata]);

  // ─── Stato calcolato + avvisi ─────────────────────────────────
  const righeConStato = useMemo(() => {
    return righeStagione.map((r) => ({
      ...r,
      _stato: calcolaStato(r, oggi),
      _allarme: isCasoAllarme(r, oggi),
      // Blocco 1 v192: avanzamento setup e sottotitolo della cella Stato
      _fasiFatte: statoSetup(r).fatte,
      _partito: isPartito(r, oggi),
      _sottotitolo: sottotitoloRiga(r, oggi),
      // v194 (D651): stesso criterio della sola lettura delle fasi in DettaglioSetup
      _conclusoIl: (r.data_fine && oggi > r.data_fine) ? r.data_fine : null,
    }));
  }, [righeStagione, oggi]);

  // v197: ordinamento colonne tabella leghe. Default alfabetico crescente.
  const [ordina, setOrdina] = useState({ col: "lega", dir: "asc" });

  const cambiaOrdine = (col) => {
    setOrdina((prec) =>
      prec.col === col
        ? { col, dir: prec.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" }
    );
  };

  const righeOrdinate = useMemo(() => {
    const seg = ordina.dir === "asc" ? 1 : -1;
    const testo = (v) => (v == null ? "" : String(v));
    const copia = [...righeConStato];
    copia.sort((a, b) => {
      if (ordina.col === "stato") {
        const d = (a._fasiFatte || 0) - (b._fasiFatte || 0);
        if (d !== 0) return d * seg;
        return a.lega.localeCompare(b.lega);
      }
      if (ordina.col === "data_inizio" || ordina.col === "data_fine") {
        const va = testo(a[ordina.col]);
        const vb = testo(b[ordina.col]);
        // Righe senza data sempre in fondo, in entrambi i versi
        if (!va && !vb) return a.lega.localeCompare(b.lega);
        if (!va) return 1;
        if (!vb) return -1;
        const d = va.localeCompare(vb);
        if (d !== 0) return d * seg;
        return a.lega.localeCompare(b.lega);
      }
      return a.lega.localeCompare(b.lega) * seg;
    });
    return copia;
  }, [righeConStato, ordina]);

  const avvisiBidir = useMemo(() => {
    const out = [];
    // TASK 17-bis v164: forzatura per stagione, non più globale
    const forzaturaData = stagioniMeta[stagioneSelezionata]?.override_data_fine || null;
    righeStagione.forEach((r) => {
      if (isOltre1Luglio(r.data_fine, r.stagione)) {
        // Se la forzatura attiva copre la data anomala, l'avviso non serve piu'
        if (forzaturaData && forzaturaData >= r.data_fine) return;
        out.push({
          lega: r.lega, tipo: "oltre",
          isoData: r.data_fine, stagione: r.stagione,
          dataSuggerita: r.data_fine,
        });
      }
      if (isPrima1Luglio(r.data_inizio, r.stagione)) {
        // Suggerimento: il giorno prima dell'inizio per portarlo nella stagione successiva
        const d = new Date(r.data_inizio);
        d.setDate(d.getDate() - 1);
        const sugg = d.toISOString().split("T")[0];
        // Se la forzatura attiva e' antecedente alla data di inizio anomala,
        // significa che il sistema sa gia' che la nuova stagione parte da quella data
        if (forzaturaData && forzaturaData < r.data_inizio) return;
        out.push({
          lega: r.lega, tipo: "prima",
          isoData: r.data_inizio, stagione: r.stagione,
          dataSuggerita: sugg,
        });
      }
    });
    return out;
  }, [righeStagione, stagioniMeta, stagioneSelezionata]);

  const legheAllarme = useMemo(
    () => righeConStato.filter((r) => r._allarme).map((r) => r.lega),
    [righeConStato]
  );

  const statoStagioneSel = useMemo(
    () => statoStagione(righeStagione.filter((r) => r.data_inizio), oggi),
    [righeStagione, oggi]
  );

  // Esiste almeno una stagione in corso da qualche parte nel sistema?
  // (per decidere se aprire il modale di conferma sul "+ Aggiungi")
  const haStagioneInCorso = useMemo(() => {
    return righe.some((r) => r.data_fine && r.data_fine >= oggi);
  }, [righe, oggi]);

  // Nome della stagione in corso più recente (mostrato nel modale)
  const stagioneInCorsoNome = useMemo(() => {
    const candidate = righe.filter((r) => r.data_fine && r.data_fine >= oggi);
    if (candidate.length === 0) return null;
    const stag = [...new Set(candidate.map((r) => r.stagione))].sort().reverse();
    return stag[0];
  }, [righe, oggi]);

  // Stagione completamente vuota? (nessuna delle 11 righe ha date inserite)
  // Usata per abilitare il pulsante "🗑 Elimina stagione".
  const stagioneVuota = useMemo(() => {
    if (!stagioneSelezionata) return false;
    const dati = righe.filter((r) => r.stagione === stagioneSelezionata);
    if (dati.length === 0) return false;
    return dati.every((r) => !r.data_inizio && !r.data_fine);
  }, [righe, stagioneSelezionata]);

  // Primo match della stagione visualizzata (per riquadro Quote)
  const primoMatch = useMemo(() => {
    const candidati = righeStagione
      .filter((r) => r.data_inizio)
      .sort((a, b) => a.data_inizio.localeCompare(b.data_inizio));
    return candidati[0] || null;
  }, [righeStagione]);

  // Stato Investisport Quote (D366)
  // Blocco 1 v192: "non_verificabile" quando la lettura dell'archivio quote
  // non riesce. Prima questo caso finiva in "azione_richiesta", cioe' la pagina
  // dichiarava il sistema indietro quando semplicemente non era riuscita a
  // controllarlo.
  const statoQuote = useMemo(() => {
    if (!stagioneSelezionata) return "azione_richiesta";
    if (statoStagioneSel === "conclusa") return "assente";
    if (!quoteStatus || quoteStatus.stato !== "ok") return "non_verificabile";
    if (quoteStatus.stagione === stagioneSelezionata) return "azione_eseguita";
    return "azione_richiesta";
  }, [quoteStatus, stagioneSelezionata, statoStagioneSel]);

  // ─── Forzatura per stagione (TASK 17-bis v164) ─────────────────
  //
  // La forzatura `override_data_fine` ora è per-stagione (tabella `stagioni_meta`)
  // invece che globale. Ogni stagione ha la sua forzatura indipendente, e
  // l'archivio conserva la storia delle forzature passate.
  const forzaturaStagione = useMemo(() => {
    if (!stagioneSelezionata) return null;
    return stagioniMeta[stagioneSelezionata]?.override_data_fine || null;
  }, [stagioniMeta, stagioneSelezionata]);
  const forzaturaAttiva = !!forzaturaStagione;

  // ─── Visibilità riquadro "Termine stagione sportiva" ──────────
  // Compare in tutti i casi TRANNE quando la stagione visualizzata e' "in preparazione".
  // Una stagione "in preparazione" non ha senso di mostrare un riquadro per la fine.
  const mostraRiquadroTermine = useMemo(() => {
    if (!stagioneSelezionata) return false;
    return statoStagioneSel !== "in preparazione";
  }, [statoStagioneSel, stagioneSelezionata]);

  // ─── Riquadro in modalita' archivio (read-only) ───────────────
  // E' archivio quando la stagione successiva (X+1) e' gia' stata caricata
  // nel sistema. In tal caso i tasti sono disabilitati e il testo descrittivo
  // racconta la "storia" della forzatura passata.
  const isArchivio = useMemo(() => {
    if (!mostraRiquadroTermine || !stagioneSelezionata) return false;
    const succ = stagioneSuccessiva(stagioneSelezionata);
    return righe.some((r) => r.stagione === succ);
  }, [mostraRiquadroTermine, stagioneSelezionata, righe]);

  // ─── Handlers ─────────────────────────────────────────────────

  const handleSalvaData = async (lega, campo, nuovaData) => {
    const payload = { lega, stagione: stagioneSelezionata };
    payload[campo] = nuovaData;
    try {
      await fetch(`${API}/impostazioni/stagioni-leghe`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      await caricaTutto();
    } catch (e) {
      console.error("Errore salva data:", e);
    }
  };

  // ─── Blocco 1 v192: spunte fasi, nota, link procedura ──────────

  // Segna o annulla una fase. Scrive la data di oggi, oppure null per annullare.
  const handleToggleFase = async (lega, campo, segnaFatta) => {
    const payload = { lega, stagione: stagioneSelezionata };
    payload[campo] = segnaFatta ? oggi : null;
    try {
      await fetch(`${API}/impostazioni/stagioni-leghe/setup`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      await ricaricaRighe();
    } catch (e) {
      console.error("Errore salva fase setup:", e);
    }
  };

  const handleSalvaNota = async (lega, testo) => {
    try {
      await fetch(`${API}/impostazioni/stagioni-leghe/setup`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lega, stagione: stagioneSelezionata, note_setup: testo,
        }),
      });
      await ricaricaRighe();
    } catch (e) {
      console.error("Errore salva nota setup:", e);
    }
  };

  const handleSalvaLinkProcedura = async (campo, valore) => {
    try {
      await fetch(`${API}/impostazioni/utente`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [campo]: valore }),
      });
      const imp = await fetch(`${API}/impostazioni/utente`).then((r) => r.json());
      setImpostazioni(imp || {});
      setToast(valore ? "Indirizzo della procedura salvato." : "Indirizzo della procedura rimosso.");
    } catch (e) {
      console.error("Errore salva link procedura:", e);
    }
  };

  const handleClickAggiungi = () => {
    // Modale di conferma scatta in due casi:
    // A) c'e' almeno una stagione in corso nel sistema (D365 originale)
    // B) la stagione attualmente selezionata e' completamente vuota
    //    (rischio: accumulare stagioni vuote a catena senza compilare)
    if (haStagioneInCorso || stagioneVuota) {
      setModaleAggiungi(true);
    } else {
      eseguiAggiungi();
    }
  };

  const eseguiAggiungi = async () => {
    const nuova = stagioneSelezionata ? stagioneSuccessiva(stagioneSelezionata) : "26-27";
    // Inserisco 11 righe vuote in stagioni_leghe
    try {
      for (const lega of LEGHE) {
        await fetch(`${API}/impostazioni/stagioni-leghe`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            lega, stagione: nuova,
            data_inizio: null, data_fine: null,
          }),
        });
      }
      await caricaTutto();
      setStagioneSelezionata(nuova);
      setModaleAggiungi(false);
      setToast(`Stagione ${nuova} creata. Inserisci le date di inizio/fine per ciascun campionato.`);
    } catch (e) {
      console.error("Errore aggiungi stagione:", e);
    }
  };

  const handleClickElimina = () => {
    setModaleElimina(true);
  };

  const eseguiElimina = async () => {
    const stagioneEliminata = stagioneSelezionata;
    // Calcolo le stagioni rimanenti PRIMA della DELETE per evitare flicker
    const stagioniRimaste = [...new Set(
      righe.filter((r) => r.stagione !== stagioneEliminata).map((r) => r.stagione)
    )].sort().reverse();
    try {
      for (const lega of LEGHE) {
        await fetch(
          `${API}/impostazioni/stagioni-leghe?lega=${encodeURIComponent(lega)}&stagione=${encodeURIComponent(stagioneEliminata)}`,
          { method: "DELETE" }
        );
      }
      // Imposto la stagione PRIMA di ricaricare per evitare il flicker del dropdown
      setStagioneSelezionata(stagioniRimaste[0] || null);
      await caricaTutto();
      setModaleElimina(false);
      setToast(`Stagione ${stagioneEliminata} eliminata.`);
    } catch (e) {
      console.error("Errore elimina stagione:", e);
    }
  };

  const handleAttivaForzaturaDaAvviso = (dataSuggerita, lega) => {
    setDataPrecompilataForzatura(dataSuggerita);
    setLegaSuggerimentoForzatura(lega);
    // Scroll al riquadro forzatura
    setTimeout(() => {
      riquadroForzaturaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const handleAttivaForzatura = async (data) => {
    if (!stagioneSelezionata) return;
    try {
      // TASK 17-bis v164: salva la forzatura sulla stagione visualizzata
      await fetch(`${API}/impostazioni/stagioni-meta/${stagioneSelezionata}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ override_data_fine: data }),
      });
      await caricaTutto();
      setDataPrecompilataForzatura("");
      setLegaSuggerimentoForzatura("");
      setToast(`Forzatura attivata al ${isoToIt(data)} per la stagione ${stagioneSelezionata}.`);
    } catch (e) {
      console.error("Errore attiva forzatura:", e);
    }
  };

  const handleAggiornaForzatura = async (data) => {
    if (!stagioneSelezionata) return;
    try {
      // TASK 17-bis v164: aggiorna la forzatura della stagione visualizzata
      await fetch(`${API}/impostazioni/stagioni-meta/${stagioneSelezionata}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ override_data_fine: data }),
      });
      await caricaTutto();
      setDataPrecompilataForzatura("");
      setLegaSuggerimentoForzatura("");
      setToast(`Data della forzatura aggiornata al ${isoToIt(data)} per la stagione ${stagioneSelezionata}.`);
    } catch (e) {
      console.error("Errore aggiorna forzatura:", e);
    }
  };

  const handleDisattivaForzatura = async () => {
    if (!stagioneSelezionata) return;
    try {
      // TASK 17-bis v164: disattiva = PUT con null (D424)
      await fetch(`${API}/impostazioni/stagioni-meta/${stagioneSelezionata}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ override_data_fine: null }),
      });
      await caricaTutto();
      setToast(`Forzatura disattivata per la stagione ${stagioneSelezionata}.`);
    } catch (e) {
      console.error("Errore disattiva forzatura:", e);
    }
  };

  // ─── Render ───────────────────────────────────────────────────

  if (caricamento) {
    return (
      <div style={S.pagina}>
        <div style={{ fontSize: 14, color: "#64748B" }}>Caricamento…</div>
      </div>
    );
  }

  return (
    <div style={S.pagina}>
      {/* Header */}
      <h1 style={{
        fontSize: 22, fontWeight: 700, color: "#1E293B",
        marginBottom: 4, letterSpacing: "-0.02em",
      }}>
        🚀 Setup nuova stagione
      </h1>
      <p style={{ fontSize: 13, color: "#64748B", marginBottom: 10 }}>
        Procedura guidata per l'avvio della nuova stagione sportiva.
      </p>

      {/* Blocco 1 v192 — link alla procedura di setup, modificabile */}
      <div style={{ marginBottom: 22 }}>
        <LinkProcedura
          etichetta="Procedura di setup nuova stagione"
          valore={impostazioni?.link_procedura_setup || null}
          onSalva={(v) => handleSalvaLinkProcedura("link_procedura_setup", v)}
        />
      </div>

      {/* Comando stagione (D362-D363) */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 16, gap: 12, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>Stagione</span>
          <TooltipStati />
          <select
            value={stagioneSelezionata || ""}
            onChange={(e) => setStagioneSelezionata(e.target.value)}
            style={{
              fontSize: 13, padding: "7px 12px",
              border: "1px solid #CBD5E1", borderRadius: 6,
              fontFamily: FONT, background: "#fff",
              minWidth: 240,
            }}
          >
            {stagioniDisponibili.map((s) => {
              const dellaStag = righe.filter((r) => r.stagione === s);
              const st = statoStagione(dellaStag.filter((r) => r.data_inizio), oggi);
              return <option key={s} value={s}>{s} ({st})</option>;
            })}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {stagioneVuota && (
            <button onClick={handleClickElimina} style={S.btnElimina}>
              🗑 Elimina stagione
            </button>
          )}
          <button onClick={handleClickAggiungi} style={S.btnPrimario}>
            + Aggiungi nuova stagione
          </button>
        </div>
      </div>

      {/* Tabella leghe */}
      <div style={S.card}>
        <table style={S.tabella}>
          <thead>
            <tr>
              <ThOrd col="lega" attiva={ordina} onClick={cambiaOrdine}>Campionato</ThOrd>
              <ThOrd col="data_inizio" attiva={ordina} onClick={cambiaOrdine}>Inizio</ThOrd>
              <ThOrd col="data_fine" attiva={ordina} onClick={cambiaOrdine}>Fine</ThOrd>
              <ThOrd col="stato" attiva={ordina} onClick={cambiaOrdine}>Stato</ThOrd>
              <th style={{ ...S.th, textAlign: "right" }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {righeOrdinate.map((r) => (
              <Fragment key={r.lega}>
              <tr style={{
                background: r._allarme ? "#FFFAF5" : "#fff",
              }}>
                <td style={{ ...S.td, fontWeight: 500 }}>{r.lega}</td>
                <td style={S.td}>
                  <CellaData
                    valore={r.data_inizio}
                    onSalva={(d) => handleSalvaData(r.lega, "data_inizio", d)}
                    conclusoIl={r._conclusoIl}
                    campo="inizio"
                  />
                </td>
                <td style={S.td}>
                  <CellaData
                    valore={r.data_fine}
                    onSalva={(d) => handleSalvaData(r.lega, "data_fine", d)}
                    conclusoIl={r._conclusoIl}
                    campo="fine"
                  />
                </td>
                {/* Blocco 1 v192 — avanzamento setup finche' il campionato non parte,
                    poi torna a raccontare la vita del campionato (in corso/terminato). */}
                <td style={S.td}>
                  {r._partito && r._fasiFatte === 4 && (
                    <BadgeStato statoKey={r._stato} allarme={r._allarme} />
                  )}
                  {r._partito && r._fasiFatte < 4 && (
                    <>
                      <BadgeSetup
                        fatte={r._fasiFatte}
                        prefisso={r._stato === "campionato_terminato" ? "terminato" : "in corso"}
                        allarme
                      />
                      <div style={{ fontSize: 11, color: "#A32D2D", marginTop: 3 }}>
                        setup non completato
                      </div>
                    </>
                  )}
                  {!r._partito && (
                    <>
                      <BadgeSetup fatte={r._fasiFatte} />
                      <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>
                        {r._sottotitolo}
                      </div>
                    </>
                  )}
                </td>
                <td style={S.tdAzioni}>
                  {(r.note_setup || "").trim() && (
                    <span
                      title={r.note_setup}
                      style={{
                        display: "inline-block", verticalAlign: "middle",
                        marginRight: 10, lineHeight: 0, cursor: "help",
                      }}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                        stroke="#BA7517" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round"
                        aria-label="contiene una nota">
                        <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                        <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
                        <path d="M9 9h1M9 13h6M9 17h6" />
                      </svg>
                    </span>
                  )}
                  <button
                    onClick={() => setDettaglioAperto(
                      dettaglioAperto === r.lega ? null : r.lega
                    )}
                    style={dettaglioAperto === r.lega ? S.btnNeutro : S.btnOutline}
                  >{dettaglioAperto === r.lega ? "Chiudi" : "Dettaglio"}</button>
                </td>
              </tr>
              {dettaglioAperto === r.lega && (
                <tr>
                  <td colSpan={5} style={{ padding: 0, borderBottom: "1px solid #E2E8F0" }}>
                    <DettaglioSetup
                      riga={r}
                      onToggleFase={handleToggleFase}
                      onSalvaNota={handleSalvaNota}
                    />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Avvisi bidirezionali (D360-D361) — nascosti quando il riquadro Termine non c'e'
          o quando siamo in archivio (la stagione successiva e' gia' stata caricata). */}
      {mostraRiquadroTermine && !isArchivio && avvisiBidir.map((a, i) => (
        <AvvisoBidirezionale
          key={`${a.lega}-${a.tipo}-${i}`}
          lega={a.lega}
          tipo={a.tipo}
          isoData={a.isoData}
          stagione={a.stagione}
          dataSuggerita={a.dataSuggerita}
          onAttivaForzatura={handleAttivaForzaturaDaAvviso}
        />
      ))}

      {/* Avviso aggregato caso d'allarme (D371) */}
      {legheAllarme.length > 0 && (
        <AvvisoAllarmeAggregato legheAllarme={legheAllarme} />
      )}

      <div style={{ marginTop: 18 }} />

      {/* Riquadro Termine stagione sportiva (D359 + TASK 17-bis v164) */}
      {mostraRiquadroTermine && (
        <RiquadroTermineStagione
          riquadroRef={riquadroForzaturaRef}
          stagioneVisualizzata={stagioneSelezionata}
          archivio={isArchivio}
          forzaturaAttiva={forzaturaAttiva}
          dataForzatura={forzaturaStagione}
          dataPrecompilata={dataPrecompilataForzatura}
          legaSuggerita={legaSuggerimentoForzatura}
          onAttiva={handleAttivaForzatura}
          onAggiorna={handleAggiornaForzatura}
          onDisattiva={handleDisattivaForzatura}
        />
      )}

      {/* Riquadro Investisport Quote (D366) */}
      <RiquadroInvestisportQuote
        stato={statoQuote}
        stagioneCorrente={stagioneSelezionata}
        primoMatchData={primoMatch?.data_inizio}
        primoMatchLega={primoMatch?.lega}
        linkProcedura={impostazioni?.link_procedura_investisport_quote || LINK_PROCEDURA_QUOTE}
        onSalvaLink={(v) => handleSalvaLinkProcedura("link_procedura_investisport_quote", v)}
        onRicontrolla={ricontrollaQuote}
        ricontrolloInCorso={ricontrolloQuote}
      />

      {/* Modale conferma aggiungi (D365 + estensione caso vuoto) */}
      {modaleAggiungi && (() => {
        const stagioneNuova = stagioneSuccessiva(stagioneSelezionata);
        let corpo;
        if (stagioneVuota) {
          // Caso B: stagione corrente vuota
          corpo = (
            <>
              La stagione <strong>{stagioneSelezionata}</strong> è ancora in preparazione
              (nessun campionato ha date inserite). Stai per creare la <strong>{stagioneNuova}</strong>:
              ti consigliamo di compilare prima le date della {stagioneSelezionata}. Vuoi procedere comunque?
            </>
          );
        } else {
          // Caso A: stagione in corso da qualche parte (D365 originale)
          corpo = (
            <>
              La stagione <strong>{stagioneInCorsoNome || stagioneSelezionata}</strong> risulta ancora in corso
              (non tutti i campionati hanno superato la propria data di fine).
              Stai creando la <strong>{stagioneNuova}</strong>: è normale farlo nei mesi di preparazione,
              ma volevamo assicurarci che fosse intenzionale.
            </>
          );
        }
        return (
          <ModaleConfermaAggiungi
            corpo={corpo}
            onAnnulla={() => setModaleAggiungi(false)}
            onConferma={eseguiAggiungi}
          />
        );
      })()}

      {/* Modale conferma elimina */}
      {modaleElimina && (
        <ModaleConfermaElimina
          stagione={stagioneSelezionata}
          onAnnulla={() => setModaleElimina(false)}
          onConferma={eseguiElimina}
        />
      )}

      {/* Toast */}
      <Toast messaggio={toast} onChiudi={() => setToast("")} />
    </div>
  );
}
